import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJournalStore } from '../hooks/useJournalStore';
import { continuationApi } from '../services/api';
import { HeaderNav } from '../components/HeaderNav';
import type { GoalFrame, ContinuationStatus } from '../types';

const STATUS_DOT: Record<ContinuationStatus, string> = {
  created: 'bg-primary',
  running: 'bg-tower-success animate-pulse-slow',
  sleeping: 'bg-tower-warning',
  waiting: 'bg-warning-bg',
  blocked: 'bg-tower-danger',
  merged: 'bg-purpleToken',
  done: 'bg-success-bg',
  killed: 'bg-error',
  failed: 'bg-tower-danger',
};

export function JournalList() {
  const { journals, loadJournals } = useJournalStore();
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newGoal, setNewGoal] = useState({ intent: '', question: '', deliverable: 'structured_evidence_brief' });

  useEffect(() => { loadJournals(); }, []);

  async function handleCreate() {
    if (!newGoal.intent || !newGoal.question) return;
    setCreating(true);
    try {
      const goalFrame: GoalFrame = {
        intent: newGoal.intent,
        subject: {},
        question: newGoal.question,
        bindings: {},
        deliverable: newGoal.deliverable,
        eligibleTools: ['rag_search'],
      };
      const c = await continuationApi.create({ goalFrame });
      await loadJournals();
      navigate(`/${c.id}`);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="min-h-screen bg-tower-bg text-tower-text">
      <header className="h-14 bg-tower-surface border-b border-tower-border flex items-center px-6 gap-4">
        <span className="text-lg font-mono font-bold text-tower-accent tracking-wider">MAESTRO JOURNALS</span>
        <div className="flex-1" />
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="text-sm px-3 py-1.5 bg-tower-accent text-textOnPrimary rounded hover:bg-tower-accent/80"
        >
          New Journal
        </button>
        <div className="h-4 w-px bg-tower-border" />
        <HeaderNav />
      </header>

      {showCreate && (
        <div className="mx-6 mt-4 p-4 bg-tower-surface border border-tower-border rounded-lg">
          <div className="space-y-3">
            <input
              placeholder="Intent (e.g., evaluate_feed_additive_effect)"
              value={newGoal.intent}
              onChange={(e) => setNewGoal(p => ({ ...p, intent: e.target.value }))}
              className="w-full bg-tower-bg border border-tower-border rounded px-3 py-2 text-sm text-tower-text focus:outline-none focus:border-tower-accent"
            />
            <textarea
              placeholder="Research question..."
              value={newGoal.question}
              onChange={(e) => setNewGoal(p => ({ ...p, question: e.target.value }))}
              className="w-full bg-tower-bg border border-tower-border rounded px-3 py-2 text-sm text-tower-text resize-none h-20 focus:outline-none focus:border-tower-accent"
            />
            <div className="flex gap-2">
              <button
                onClick={handleCreate}
                disabled={creating || !newGoal.intent || !newGoal.question}
                className="px-4 py-2 bg-tower-accent text-textOnPrimary text-sm rounded hover:bg-tower-accent/80 disabled:opacity-40"
              >
                {creating ? 'Creating...' : 'Create Journal'}
              </button>
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-tower-muted text-sm hover:text-tower-text">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="p-6 grid gap-3">
        {journals.length === 0 && (
          <div className="text-center text-tower-muted py-12">
            No journals yet. Create one to start researching.
          </div>
        )}
        {journals.map((j) => (
          <button
            key={j.rootId}
            onClick={() => navigate(`/${j.rootId}`)}
            className="text-left p-4 bg-tower-surface border border-tower-border rounded-lg hover:border-tower-accent/50 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-2 h-2 rounded-full ${STATUS_DOT[j.status]}`} />
                  <span className="text-sm font-mono text-tower-accent">{j.intent}</span>
                </div>
                <p className="text-sm text-tower-text">{j.question}</p>
              </div>
              <div className="text-right text-xs text-tower-muted space-y-0.5 ml-4 shrink-0">
                <div>${j.dollarsSpent.toFixed(2)}</div>
                <div>{j.findingsCount} findings</div>
                <div>{j.descendantCount} continuations</div>
              </div>
            </div>
            <div className="flex gap-3 mt-2 text-xs text-tower-muted">
              <span className="text-tower-success">{j.active} active</span>
              <span className="text-tower-warning">{j.sleeping} sleeping</span>
              <span className="text-warning">{j.waiting} waiting</span>
              <span className="ml-auto">{new Date(j.updatedAt).toLocaleString()}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
